import {AuthConsumerRenderProps, withAuthContext} from "auth/AuthContext";
import StyledErrorMessage from "component/StyledErrorMessage";
import React, {Component} from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import {RouteComponentProps} from "react-router";
import ContainerInstructionsView from "route/person-onboard-container/ContainerInstructionsView";
import ContainerPersonDetails from "route/person-onboard-container/ContainerPersonDetails";
import ContainerPersonOnboardContainer from "route/person-onboard-container/ContainerPersonOnboardContainer";
import ContainerValidationView from "route/person-onboard-container/ContainerValidationView";
import {convertverifyOnboardingToken} from "service/onboardingServices";
import {UpsertEmployeeDto} from "ts-types/api.types";
import {withRouterWorkaround} from "util/workaroundUtils";

export enum ContainerPersonOnboardViews {
  INSTRUCTIONS = "INSTRUCTIONS",
  VALIDATION = "VALIDATION",
  PERSON_DETAILS = "PERSON_DETAILS"
}

interface Props extends RouteComponentProps<any>,
    AuthConsumerRenderProps,
    WithTranslation {

}

interface State {
  activeView: ContainerPersonOnboardViews,
  personOnboarding: Partial<UpsertEmployeeDto>,
  token?: string,
  errorMessages: Array<string>
  personEmail: string
}

class ContainerPersonOnboardView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    let activeView = ContainerPersonOnboardViews.INSTRUCTIONS;
    const container = props.accessContainerData;
    if (container && container.type === "SWS") {
      activeView = ContainerPersonOnboardViews.VALIDATION;
    }

    this.state = {
      activeView: activeView,
      personOnboarding: this.initialPersonOnboardingValues(),
      errorMessages: [],
      personEmail: ""
    };
  }

  initialPersonOnboardingValues = (): Partial<UpsertEmployeeDto> => {
    return {
      companyEmail: "",
      name: "",
      firstName: "",
      birthDate: "",
      sex: 2,
      telephone: "",
      language: "de"
    };
  };

  setActiveView = (activeView: ContainerPersonOnboardViews) => {
    this.setState({
      activeView: activeView
    });
  };

  setPersonOnboardingData = (data: Partial<UpsertEmployeeDto>) => {
    this.setState({
      personOnboarding: data
    });
  };

  setPersonEmail = (email: string) => {
    this.setState({
      personEmail: email
    });
  };

  setToken = (token: string) => {
    this.setState({
      token: token
    });
  };

  verifyCode = async () => {

    const token = this.state.token;
    if (!token) {
      return;
    }

    try {

      const accessToken = await convertverifyOnboardingToken(token);

      await this.props.verifyToken(accessToken);

      this.props.history.push("/", {welcome: "true"});

    } catch (e) {
      const {response} = e;
      if (response?.data?.error) {
        const {error} = response.data;

        if (["INVALID_VERIFICATION_CODE"].includes(error)) {
          this.props.history.push("/verification-code", {token: token});
          return;
        } else if (["MULTIPLE_CANDIDATE_ACCOUNTS"].includes(error)) {
          this.props.history.push("/account-selection", {token: token});
          return;
        }
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {

    const {errorMessages} = this.state;

    if (errorMessage) {

      const errMsgs = [...errorMessages];
      errMsgs.push(errorMessage);

      this.setState({
        errorMessages: errMsgs
      });
    } else {

      this.setState({
        errorMessages: []
      });
    }
  };

  renderActiveView = () => {

    const {personOnboarding, activeView, personEmail} = this.state;

    if (personEmail) {
      personOnboarding.companyEmail = personEmail;
    }

    switch (activeView) {
      case ContainerPersonOnboardViews.VALIDATION: {
        return <ContainerValidationView
            setActiveView={this.setActiveView}
            setCompanyEmail={this.setPersonEmail}
            setToken={this.setToken}
        />;
      }

      case ContainerPersonOnboardViews.PERSON_DETAILS: {
        return <ContainerPersonDetails
            personOnboarding={personOnboarding}
            setPersonOnboardingData={this.setPersonOnboardingData}
            setActiveView={this.setActiveView}
            onRegistrationSuccess={this.verifyCode}
            setErrorMessage={this.setErrorMessage}
        />;
      }

      default: {
        return <ContainerInstructionsView
            setActiveView={this.setActiveView}
        />;
      }
    }
  };

  render(): React.ReactNode {

    const {errorMessages, activeView} = this.state;

    return (
        <ContainerPersonOnboardContainer titleKey={`personOnboard.${activeView}.title`} activeView={activeView}>
          {errorMessages.length > 0 &&
          <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
            {errorMessages.map(err => <div key={err}>{err}</div>)}
          </StyledErrorMessage>
          }
          {this.renderActiveView()}
        </ContainerPersonOnboardContainer>
    );
  }
}

export default withRouterWorkaround(
    withAuthContext(
        withTranslation(["login"])(
            ContainerPersonOnboardView)));